/**
 * Wandelt die aufgelösten (für einen Monat gültigen) Einstellungen — ein flaches
 * `Record<schluessel, wert>` aus `einstellungAllEffective` — in eine typisierte
 * `EngineKonfig`. Fehlende Schlüssel fallen auf die gesetzlichen Defaults zurück
 * (KONZEPT §6 / defaults.ts), damit die Engine nie undefiniert rechnet.
 *
 * **Seit AP-22 Teil 6 sind diese Rückfälle die Bestandswerte, nicht der KV-Standard.** Der
 * Standard eines Kollektivvertrags kommt über die Vorlage (`kv/`) in die Auflösung; was hier
 * als Rückfall steht, ist der Wert, mit dem der Kern rechnet wie vor dem Schlüssel. Damit
 * rechnet alles, was einen neuen Schlüssel nicht kennt, unverändert weiter — die
 * bestehenden Tests und jeder Monat, der vor dem Schlüssel eingefroren wurde.
 */
import { zulageSchluessel, type ZulageDef } from './kv/typen'
import type { Betriebszeit, EngineKonfig, UeberstundenZeitraum, ZulageKonfig, ZuschlagRegel } from './types'

const ZEITRAEUME: UeberstundenZeitraum[] = ['taeglich', 'woechentlich', 'monatlich', 'monate']

/** Abrechnungszeitraum aus dem Einstellungswert; unbekannte Werte → wöchentlich. */
function zeitraum(v: string | undefined): UeberstundenZeitraum {
  const k = (v ?? '').trim() as UeberstundenZeitraum
  return ZEITRAEUME.includes(k) ? k : 'woechentlich'
}

function zahl(v: string | undefined, fallback: number): number {
  if (v === undefined) return fallback
  const n = Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : fallback
}

function bool(v: string | undefined, fallback: boolean): boolean {
  if (v === undefined) return fallback
  return v === '1' || v.toLowerCase() === 'true'
}

function text(v: string | undefined, fallback: string): string {
  return v && v.trim() !== '' ? v.trim() : fallback
}

/** Zuschlagsregeln aus JSON parsen (defensiv: nur wohlgeformte Regeln übernehmen). */
function parseRegeln(roh: string | undefined): ZuschlagRegel[] {
  if (!roh) return []
  try {
    const arr = JSON.parse(roh)
    if (!Array.isArray(arr)) return []
    const regeln: ZuschlagRegel[] = []
    for (const r of arr) {
      if (r && r.art === 'zeit' && typeof r.von === 'string' && typeof r.bis === 'string') {
        regeln.push({
          art: 'zeit',
          von: r.von,
          bis: r.bis,
          prozent: Number(r.prozent) || 0,
          ...(r.nur === 'ueberstunden' ? { nur: 'ueberstunden' as const } : {}),
        })
      } else if (r && r.art === 'ueberstunde_ab') {
        regeln.push({
          art: 'ueberstunde_ab',
          abStunde: Number(r.abStunde) || 1,
          prozent: Number(r.prozent) || 0,
        })
      }
    }
    return regeln
  } catch {
    return []
  }
}

/** Standard-Betriebszeiten: Mo–Fr 07:00–17:00, Sa/So kein Betrieb. */
export const BETRIEBSZEITEN_DEFAULT: (Betriebszeit | null)[] = [
  { von: '07:00', bis: '17:00' },
  { von: '07:00', bis: '17:00' },
  { von: '07:00', bis: '17:00' },
  { von: '07:00', bis: '17:00' },
  { von: '07:00', bis: '17:00' },
  null,
  null,
]

/**
 * Betriebszeiten aus JSON parsen: Liste mit 7 Einträgen (Index 0 = Montag),
 * je `{von,bis}` oder null. Unvollständige/kaputte Angaben → Default.
 */
export function parseBetriebszeiten(roh: string | undefined): (Betriebszeit | null)[] {
  if (!roh) return BETRIEBSZEITEN_DEFAULT
  try {
    const arr = JSON.parse(roh)
    if (!Array.isArray(arr)) return BETRIEBSZEITEN_DEFAULT
    const out: (Betriebszeit | null)[] = []
    for (let i = 0; i < 7; i++) {
      const e = arr[i]
      out.push(
        e && typeof e.von === 'string' && typeof e.bis === 'string'
          ? { von: e.von, bis: e.bis }
          : null
      )
    }
    return out
  } catch {
    return BETRIEBSZEITEN_DEFAULT
  }
}

/**
 * Die eingeschalteten Zulagen eines Katalogs. Ohne Schalter in den Werten ist eine Zulage
 * **aus** — das ist ihr Bestandswert: Ein Monat, der vor den Zulagen eingefroren wurde,
 * trägt die Schlüssel nicht und bekommt auch nachträglich keine.
 */
function parseZulagen(werte: Record<string, string>, katalog: readonly ZulageDef[]): ZulageKonfig[] {
  const aus: ZulageKonfig[] = []
  for (const z of katalog) {
    const k = zulageSchluessel(z.schluessel)
    if (!bool(werte[k.aktiv], false)) continue
    aus.push({
      schluessel: z.schluessel,
      bemessung: z.bemessung,
      satz: Math.max(0, zahl(werte[k.satz], 0)),
      prozent: z.prozent === true,
      ...(z.bemessung === 'fensterstunden_ohne_ueberstunden' ? { von: text(werte[k.von], '22:00'), bis: text(werte[k.bis], '06:00') } : {}),
      ...(z.entfaelltBei ? { entfaelltBei: z.entfaelltBei } : {}),
    })
  }
  return aus
}

/**
 * @param werte   aufgelöste Einstellungen zum Stichtag
 * @param zulagen der Zulagenkatalog der geltenden KV-Vorlage; ohne ihn rechnet die Engine keine Zulagen
 */
export function parseKonfig(werte: Record<string, string>, zulagen: readonly ZulageDef[] = []): EngineKonfig {
  return {
    zulagen: parseZulagen(werte, zulagen),

    pauseAktiv: bool(werte['pause_aktiv'], true),
    pauseDauerStd: zahl(werte['pause_dauer'], 30) / 60,
    pauseSchwelleStd: zahl(werte['pause_schwelle'], 6),

    ueberstundenZeitraum: zeitraum(werte['ueberstunden_zeitraum']),
    ueberstundenZeitraumMonate: Math.max(
      2,
      Math.round(zahl(werte['ueberstunden_zeitraum_monate'], 3))
    ),

    mehrarbeitSchwelle: zahl(werte['mehrarbeit_schwelle'], 38.5),
    ueberstundenSchwelle: zahl(werte['ueberstunden_schwelle'], 40),
    mehrzeitZuschlagVollzeit: zahl(werte['mehrzeit_zuschlag_vollzeit'], 50),
    mehrzeitZuschlagTeilzeit: zahl(werte['mehrzeit_zuschlag_teilzeit'], 25),
    umrechnungZaehler: zahl(werte['umrechnung_zaehler'], 167),
    umrechnungNenner: zahl(werte['umrechnung_nenner'], 143),

    zuschlagUeberstunde: zahl(werte['zuschlag_ueberstunde'], 50),
    zuschlagRegeln: parseRegeln(werte['zuschlag_regeln']),
    sonntagProzent: zahl(werte['sonntag_prozent'], 100),
    feiertagProzent: zahl(werte['feiertag_prozent'], 100),
    zuschlagAusserhalbBetriebszeit: zahl(werte['zuschlag_ausserhalb_betriebszeit'], 50),
    betriebszeiten: parseBetriebszeiten(werte['betriebszeiten']),

    montagezulageSatz: zahl(werte['montagezulage_satz'], 1.155),

    entfernungKlein: zahl(werte['entfernungszulage_klein'], 11.94),
    entfernungMittel: zahl(werte['entfernungszulage_mittel'], 30),
    entfernungGross: zahl(werte['entfernungszulage_gross'], 62.04),
    entfernungSchwelleKlein: zahl(werte['entfernungszulage_schwelle_klein'], 6),
    entfernungSchwelleMittel: zahl(werte['entfernungszulage_schwelle_mittel'], 11),
    entfernungAnreiseGrenze: text(werte['entfernungszulage_anreise_grenze'], '11:00'),
    entfernungRueckreiseGrenze: text(werte['entfernungszulage_rueckreise_grenze'], '17:00'),

    entfernungKleinMitPause: bool(werte['entfernungszulage_klein_mit_pause'], false),
    entfernungVerpflegungProzent: zahl(werte['entfernungszulage_verpflegung_prozent'], 40),
    mehrarbeitUeber50IstUeberstunde: bool(werte['mehrarbeit_ueber_50_ist_ueberstunde'], false),
    ueberstundeAbZaehltMehrarbeit: bool(werte['ueberstunde_ab_zaehlt_mehrarbeit'], false),
    ueberstunde100ArbeitsfreiAbStunde: Math.max(0, zahl(werte['ueberstunde_100_arbeitsfrei_ab_stunde'], 0)),
    ueberstunde100AbWochenstunde: Math.max(0, zahl(werte['ueberstunde_100_ab_wochenstunde'], 0)),
    halbtagAktiv: bool(werte['halbtag_aktiv'], false),
    halbtagEnde: text(werte['halbtag_ende'], '12:00'),
    halbtagProzent: zahl(werte['halbtag_prozent'], 100),
    rueckrufProzent: zahl(werte['rueckruf_prozent'], 100),

    rundungAktiv: bool(werte['rundung_aktiv'], false),
    rundungSchrittMin: zahl(werte['rundung_schritt'], 15),
  }
}
